/**
 * University API Client
 * Handles all API calls to the university backend
 */

import type { University, FilterOptions, BrowseFilters, BrowseResult } from '../types';

const API_BASE_URL = '/api/university';

class UniversityAPI {
  /**
   * Browse universities with filters
   */
  async browseUniversities(filters: BrowseFilters): Promise<BrowseResult> {
    const params = new URLSearchParams();

    if (filters.field) params.append('field', filters.field);
    if (filters.country) params.append('country', filters.country);
    if (filters.budgetMin) params.append('budget_min', filters.budgetMin.toString());
    if (filters.budgetMax) params.append('budget_max', filters.budgetMax.toString());
    if (filters.rankingMax) params.append('ranking_max', filters.rankingMax.toString());
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.sort) params.append('sort', filters.sort);

    const response = await fetch(`${API_BASE_URL}/browse?${params.toString()}`);

    if (!response.ok) {
      throw new Error('Failed to browse universities');
    }

    const data = await response.json();
    return data.data;
  }

  /**
   * Get university by ID
   */
  async getUniversityById(id: number): Promise<University> {
    const response = await fetch(`${API_BASE_URL}/${id}`);

    if (!response.ok) {
      throw new Error('Failed to fetch university');
    }

    const data = await response.json();
    return data.data;
  }

  /**
   * Get filter options
   */
  async getFilterOptions(): Promise<FilterOptions> {
    const response = await fetch(`${API_BASE_URL}/filters/options`);

    if (!response.ok) {
      throw new Error('Failed to fetch filter options');
    }

    const data = await response.json();
    return data.data;
  }
}

export const universityAPI = new UniversityAPI();
